import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareMenu from "@/components/feature/ShareMenu";
import EpisodeList from "@/pages/anime/detail/components/EpisodeList";
import RelatedAnime from "@/pages/anime/detail/components/RelatedAnime";
import { animeList } from "@/mocks/anime";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function AnimeDetail() {
  const { id } = useParams();
  const { isFavorite, toggle } = useFavorites();
  const anime = animeList.find((a) => a.id === id);

  useDocumentMeta(
    anime ? `《${anime.title}》详情 - 51动漫` : "动漫详情 - 51动漫",
    anime
      ? `${anime.summary} 《${anime.title}》是一部${anime.type}题材的${anime.region}动漫，${anime.year}年播出，目前${anime.status}。`
      : "动漫详情 - 51动漫"
  );

  const related = useMemo(() => {
    if (!anime) return [];
    const sameType = animeList.filter((a) => a.id !== anime.id && a.type === anime.type);
    const others = animeList
      .filter((a) => a.id !== anime.id && a.type !== anime.type)
      .sort((a, b) => b.popularity - a.popularity);
    return [...sameType, ...others].slice(0, 8);
  }, [anime]);

  if (!anime) {
    return (
      <div className="w-full px-4 md:px-8 py-16 min-h-[60vh] flex flex-col items-center justify-center text-center">
        <span className="w-16 h-16 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mb-4">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-950 mb-2">未找到该动漫</h1>
        <p className="text-sm text-foreground-500 mb-5">你访问的动漫可能已下线或链接有误。</p>
        <Link
          to="/anime"
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回动漫列表
        </Link>
      </div>
    );
  }

  const favorite = isFavorite(anime.id);

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "动漫", to: "/anime" },
          { label: anime.type, to: `/anime?type=${encodeURIComponent(anime.type)}` },
          { label: anime.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 md:gap-10">
        {/* Cover */}
        <div className="mx-auto w-56 sm:w-64 lg:w-full">
          <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
            <img
              src={anime.cover}
              alt={`${anime.title} 高清封面`}
              title={`${anime.title} - 51动漫`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Info */}
        <div className="min-w-0">
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 mb-3">
            {anime.title}
          </h1>

          <div className="flex items-center flex-wrap gap-2 mb-4">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold">
              <i className="ri-star-fill"></i>
              {anime.rating.toFixed(1)} 分
            </span>
            <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
              {anime.type}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 text-xs">
              {anime.region}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 text-xs">
              {anime.year} 年
            </span>
            <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
              {anime.status}
            </span>
          </div>

          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { label: "状态", value: anime.status },
              { label: "集数", value: `${anime.episodes} / ${anime.totalEpisodes} 集` },
              { label: "地区", value: anime.region },
              { label: "更新时间", value: anime.updatedAt },
            ].map((item) => (
              <div key={item.label} className="rounded-lg bg-background-100 border border-background-200 px-3 py-2.5">
                <dt className="text-[11px] text-foreground-500 mb-0.5">{item.label}</dt>
                <dd className="text-sm font-semibold text-foreground-950 truncate">{item.value}</dd>
              </div>
            ))}
          </dl>

          <div className="flex items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => toggle(anime.id)}
              className={`inline-flex items-center gap-1.5 h-10 px-5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                favorite
                  ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                  : "border border-accent-300 text-accent-600 hover:bg-accent-50"
              }`}
            >
              <i className={favorite ? "ri-heart-fill" : "ri-heart-line"}></i>
              {favorite ? "已收藏" : "收藏"}
            </button>
            <ShareMenu title={anime.title} variant="solid" />
          </div>

          <section className="mb-2">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-2">剧情简介</h2>
            <p className="text-sm md:text-base leading-relaxed text-foreground-700">
              {anime.synopsis}
            </p>
          </section>

          <div className="mt-4 flex flex-wrap gap-2">
            {anime.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-600"
              >
                # {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Episodes */}
      <section className="mt-10 md:mt-14">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5 flex items-center gap-2">
          <i className="ri-play-list-2-line text-primary-500"></i>
          章节剧集
        </h2>
        <EpisodeList episodes={anime.episodesList} animeTitle={anime.title} />
      </section>

      <RelatedAnime list={related} />
    </div>
  );
}